import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
// @ts-ignore
import { sys } from '@netbasequid/theme'
// @ts-ignore
import { Avatar, TwitterBlueVerified, Tooltip, Skeleton, DefaultAvatar } from '@netbasequid/core'
import { useDisplayTheme } from 'hooks/useUiState'
import { useHorizontalOverflowOnResize } from 'hooks/useResizeObserver'
import { AUTO_ATTR_KEY, AutomationId } from 'utils/automationUtil'
import Link from './Link'
import { useContextMediaDetail } from './context'

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const Metadata = styled.div`
  flex: 1;
  padding: 2px 0px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const NameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  overflow: hidden;
`

const Name = styled.div.attrs({ [AUTO_ATTR_KEY]: AutomationId.popularMedia.authorName })<{
  mode: string
}>`
  color: ${({ mode }) => sys[mode].interaction.primary.default};
  font-size: ${sys.typescale[14].size};
  line-height: ${sys.typescale[14].lineHeight};
  font-weight: ${sys.fontWeight.regular};
  overflow: hidden;
  text-overflow: ellipsis;
`

const NameLink = styled(Link)`
  white-space: nowrap;
`

const Icon = styled.div`
  display: flex;
  align-items: center;
`

const Handle = styled.div.attrs({ [AUTO_ATTR_KEY]: AutomationId.popularMedia.authorHandle })<{
  mode: string
}>`
  color: ${({ mode }) => sys[mode].text.placeholder};
  font-size: ${sys.typescale[14].size};
  line-height: ${sys.typescale[14].lineHeight};
  font-weight: ${sys.fontWeight.regular};
  overflow: hidden;
  text-overflow: ellipsis;
`

interface AuthorProps {
  className?: string
}

const Author = ({ className }: AuthorProps) => {
  const displayTheme = useDisplayTheme()
  const {
    avatarUrl,
    authorName,
    authorHandle,
    authorPageUrl,
    isVerifiedAccount
  } = useContextMediaDetail()
  const [nameRef, isNameOverflow] = useHorizontalOverflowOnResize()
  const [handleRef, isHandleOverflow] = useHorizontalOverflowOnResize()
  const { t } = useTranslation()
  const shouldUseCustomFallback = !authorName && !authorHandle
  return (
    <Container className={className}>
      <Link href={authorPageUrl}>
        <Avatar
          mode={displayTheme}
          variant="circular"
          alt={authorName || authorHandle}
          diameter="48px"
          src={avatarUrl}
          loader={
            <Skeleton
              mode={displayTheme}
              variation="circle"
              width="48px"
              height="48px"
              animation="wave"
            />
          }
          fallbackStyles={{ fontSize: '24px', fontWeight: 400, cursor: 'pointer' }}>
          {shouldUseCustomFallback ? <DefaultAvatar mode={displayTheme} fontSize={48} /> : null}
        </Avatar>
      </Link>
      <Metadata>
        <NameContainer>
          <Name mode={displayTheme} ref={nameRef}>
            <Tooltip mode={displayTheme} enabled={isNameOverflow} content={authorName}>
              <NameLink href={authorPageUrl}>{authorName}</NameLink>
            </Tooltip>
          </Name>
          {isVerifiedAccount && (
            <Icon>
              <Tooltip mode={displayTheme} content={t('verifiedAccount')}>
                <TwitterBlueVerified mode={displayTheme} />
              </Tooltip>
            </Icon>
          )}
        </NameContainer>
        <Handle mode={displayTheme} ref={handleRef}>
          <Tooltip mode={displayTheme} enabled={isHandleOverflow} content={authorHandle}>
            <span>{authorHandle}</span>
          </Tooltip>
        </Handle>
      </Metadata>
    </Container>
  )
}

export default Author
